import React from 'react';
import { View, Text, TouchableOpacity, StyleSheet } from 'react-native';

/**
 * ScorePanel - Componente per visualizzare e modificare il punteggio di una squadra
 * 
 * Props:
 * - team: Oggetto squadra { name, members }
 * - teamId: 'A' o 'B'
 * - score: Punteggio corrente
 * - color: Colore primario del pannello
 * - isHighlighted: Se true, evidenzia il pannello (ultima modifica)
 * - penalties: { yellow: number, red: number }
 * - onIncrease: Callback per incremento punteggio
 * - onDecrease: Callback per decremento punteggio
 * - onNumpadOpen: Callback per aprire numpad
 * - competitionType: 'team' | 'individual' | 'mixed'
 * - maxScore: Punteggio massimo raggiungibile
 * - disabled: Se true, disabilita i controlli
 */
const ScorePanel = ({
    team,
    teamId,
    score = 0,
    color = '#1976d2',
    isHighlighted = false,
    penalties = { yellow: 0, red: 0 },
    onIncrease,
    onDecrease,
    onNumpadOpen,
    competitionType = 'team',
    maxScore = 45,
    disabled = false,
}) => {
    // Calcola il nome da visualizzare
    const displayName = React.useMemo(() => {
        if (!team) return '?';

        const name = team.name || '?';

        if (competitionType === 'individual') {
            // Per gare individuali, rimuovi "Squadra" dal nome
            return name.replace(/^\s*Squadra\s*/i, '');
        }

        return `Squadra ${name}`;
    }, [team, competitionType]);

    // Stile dinamico per evidenziazione
    const highlightStyle = isHighlighted ? {
        borderWidth: 3,
        borderColor: color,
        boxShadow: `0px 0px 12px ${color}55`,
    } : {};

    return (
        <View style={[styles.container, highlightStyle]}>
            {/* Header squadra */}
            <View style={[styles.header, { backgroundColor: color }]}>
                <Text
                    style={styles.teamName}
                    numberOfLines={2}
                    adjustsFontSizeToFit
                    minimumFontScale={0.6}
                >
                    {displayName}
                </Text>

                {/* Indicatore penalità */}
                {(penalties.yellow > 0 || penalties.red > 0) && (
                    <View style={styles.penaltyIndicator}>
                        {penalties.yellow > 0 && (
                            <View style={[styles.penaltyBadge, styles.yellowBadge]}>
                                <Text style={styles.penaltyText}>{penalties.yellow}</Text>
                            </View>
                        )}
                        {penalties.red > 0 && (
                            <View style={[styles.penaltyBadge, styles.redBadge]}>
                                <Text style={styles.penaltyText}>{penalties.red}</Text>
                            </View>
                        )}
                    </View>
                )}
            </View>

            {/* Punteggio */}
            <View style={styles.scoreContainer}>
                {/* Pulsante decremento */}
                <TouchableOpacity
                    style={[styles.scoreButton, styles.decreaseButton, disabled && styles.disabled]}
                    onPress={onDecrease}
                    disabled={disabled || score <= 0}
                >
                    <Text style={styles.scoreButtonText}>−</Text>
                </TouchableOpacity>

                {/* Punteggio centrale (cliccabile per numpad) */}
                <TouchableOpacity
                    style={[
                        styles.scoreDisplay,
                        isHighlighted && { backgroundColor: `${color}15` }
                    ]}
                    onPress={onNumpadOpen}
                    disabled={disabled}
                >
                    <Text style={[styles.scoreText, { color }]}>
                        {score}
                    </Text>
                </TouchableOpacity>

                {/* Pulsante incremento */}
                <TouchableOpacity
                    style={[styles.scoreButton, styles.increaseButton, disabled && styles.disabled]}
                    onPress={onIncrease}
                    disabled={disabled || score >= maxScore}
                >
                    <Text style={styles.scoreButtonText}>+</Text>
                </TouchableOpacity>
            </View>

            {/* Barra progresso */}
            <View style={styles.progressContainer}>
                <View
                    style={[
                        styles.progressBar,
                        {
                            width: `${Math.min((score / maxScore) * 100, 100)}%`,
                            backgroundColor: color
                        }
                    ]}
                />
            </View>
        </View>
    );
};

const styles = StyleSheet.create({
    container: {
        backgroundColor: '#fff',
        borderRadius: 16,
        overflow: 'hidden',
        marginHorizontal: 8,
        marginVertical: 8,
        minWidth: 140,
        maxWidth: 180,
        boxShadow: '0px 2px 8px rgba(0, 0, 0, 0.12)',
    },
    header: {
        paddingVertical: 12,
        paddingHorizontal: 10,
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: 50,
    },
    teamName: {
        color: '#fff',
        fontSize: 16,
        fontWeight: 'bold',
        textAlign: 'center',
        flexWrap: 'wrap',
    },
    penaltyIndicator: {
        flexDirection: 'row',
        position: 'absolute',
        top: 4,
        right: 4,
    },
    penaltyBadge: {
        width: 18,
        height: 18,
        borderRadius: 9,
        alignItems: 'center',
        justifyContent: 'center',
        marginLeft: 2,
    },
    yellowBadge: {
        backgroundColor: '#ffeb3b',
    },
    redBadge: {
        backgroundColor: '#f44336',
    },
    penaltyText: {
        fontSize: 10,
        fontWeight: 'bold',
        color: '#000',
    },
    scoreContainer: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'center',
        paddingVertical: 16,
        paddingHorizontal: 8,
    },
    scoreButton: {
        width: 44,
        height: 44,
        borderRadius: 22,
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#f0f0f0',
    },
    decreaseButton: {
        marginRight: 8,
    },
    increaseButton: {
        marginLeft: 8,
    },
    scoreButtonText: {
        fontSize: 24,
        fontWeight: 'bold',
        color: '#333',
    },
    scoreDisplay: {
        paddingHorizontal: 16,
        paddingVertical: 8,
        borderRadius: 12,
        minWidth: 60,
        alignItems: 'center',
    },
    scoreText: {
        fontSize: 42,
        fontWeight: 'bold',
    },
    progressContainer: {
        height: 4,
        backgroundColor: '#e0e0e0',
        width: '100%',
    },
    progressBar: {
        height: '100%',
        borderRadius: 2,
    },
    disabled: {
        opacity: 0.4,
    },
});

export default ScorePanel;
